import { useEffect, useState, useMemo } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateField } from "@/components/DateField";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { formatMoney, INVESTMENT_TYPES, STATUS_LABELS, STATUS_COLORS, INFRA_STATUSES } from "@/lib/constants";
import { Plus, Edit, Trash2 } from "lucide-react";
import { useQuickAdd } from "@/lib/useQuickAdd";
import { ApplicationFields, APPLICATION_DEFAULTS } from "@/components/ApplicationFields";

const CURRENT_YEAR = new Date().getFullYear();

function emptyInv() {
  return {
    village_id: "", institution_id: "", title: "", investment_type: "dogrudan",
    status: "planlama", budget: 0, actual: 0,
    start_date: "", end_date: "", year: CURRENT_YEAR, location: "", description: "",
    quantity: 0, unit: "", unit_price: 0, ...APPLICATION_DEFAULTS,
  };
}

export default function InvestmentsPage() {
  const [items, setItems] = useState([]);
  const [villages, setVillages] = useState([]);
  const [insts, setInsts] = useState([]);
  const [year, setYear] = useState(CURRENT_YEAR);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyInv());

  const villageMap = useMemo(() => Object.fromEntries(villages.map((v) => [v.id, v.name])), [villages]);
  const instMap = useMemo(() => Object.fromEntries(insts.map((i) => [i.id, i.name])), [insts]);

  const load = () => api.get("/investments", { params: { year } }).then((r) => setItems(r.data));
  useEffect(() => {
    api.get("/villages").then((r) => setVillages(r.data));
    api.get("/institutions").then((r) => setInsts(r.data));
  }, []);
  useEffect(() => { load(); }, [year]);

  const openNew = () => { setEditing(null); setForm({ ...emptyInv(), year }); setOpen(true); };
  const quickDone = useQuickAdd((vid) => { setEditing(null); setForm({ ...emptyInv(), village_id: vid }); setOpen(true); }, open);
  const openEdit = (i) => { setEditing(i); setForm({ ...emptyInv(), ...i }); setOpen(true); };

  const save = async () => {
    if (!form.title || !form.village_id) return toast.error("Köy ve başlık zorunlu");
    try {
      if (editing) await api.put(`/investments/${editing.id}`, form);
      else await api.post("/investments", form);
      toast.success(editing ? "Güncellendi" : "Oluşturuldu");
      setOpen(false); load();
      if (!editing) quickDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  const remove = async (i) => {
    if (!window.confirm("Yatırım silinsin mi?")) return;
    await api.delete(`/investments/${i.id}`);
    toast.success("Silindi"); load();
  };

  const totals = useMemo(() => {
    const t = { budget: 0, actual: 0 };
    items.forEach((i) => { t.budget += i.budget || 0; t.actual += i.actual || 0; });
    return t;
  }, [items]);

  const years = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);

  return (
    <AppLayout
      title="Yatırım Takibi"
      subtitle="Doğrudan ve ihaleli yatırımlar — bütçe ve gerçekleşen karşılaştırma"
      actions={
        <div className="flex items-center gap-2">
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-32" data-testid="investment-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={openNew} data-testid="new-investment-button" className="bg-emerald-700 hover:bg-emerald-800">
            <Plus className="h-4 w-4 mr-1.5" /> Yeni Yatırım
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card className="p-6 border border-slate-200 shadow-sm">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Toplam Bütçe</div>
          <div className="font-display text-2xl font-bold text-slate-900 mt-2">{formatMoney(totals.budget)}</div>
        </Card>
        <Card className="p-6 border border-slate-200 shadow-sm">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Gerçekleşen</div>
          <div className="font-display text-2xl font-bold text-emerald-700 mt-2">{formatMoney(totals.actual)}</div>
        </Card>
        <Card className="p-6 border border-slate-200 shadow-sm">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Gerçekleşme Oranı</div>
          <div className="font-display text-2xl font-bold text-blue-700 mt-2">
            {totals.budget ? Math.round((totals.actual / totals.budget) * 100) : 0}%
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-3">
            <div className="h-full bg-emerald-600"
                 style={{ width: `${totals.budget ? Math.min(100, (totals.actual / totals.budget) * 100) : 0}%` }} />
          </div>
        </Card>
      </div>

      <Card className="border border-slate-200 shadow-sm overflow-hidden">
        <Table data-testid="investments-table">
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Başlık</TableHead>
              <TableHead>Köy</TableHead>
              <TableHead>Kurum</TableHead>
              <TableHead>Tür</TableHead>
              <TableHead>Durum</TableHead>
              <TableHead className="text-right">Bütçe</TableHead>
              <TableHead className="text-right">Gerçekleşen</TableHead>
              <TableHead className="w-24">İşlem</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="row-hover">
            {items.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">
                Bu yıl için yatırım bulunmuyor.
              </TableCell></TableRow>
            )}
            {items.map((i) => (
              <TableRow key={i.id} data-testid={`investment-row-${i.id}`}>
                <TableCell className="font-medium">
                  {i.title}
                  {i.location && <div className="text-xs text-slate-500 font-normal" data-testid={`investment-location-${i.id}`}>Yer: {i.location}</div>}
                </TableCell>
                <TableCell>{villageMap[i.village_id] || "-"}</TableCell>
                <TableCell>{instMap[i.institution_id] || "-"}</TableCell>
                <TableCell><Badge variant="outline">{INVESTMENT_TYPES[i.investment_type]}</Badge></TableCell>
                <TableCell>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${STATUS_COLORS[i.status]}`}>
                    {STATUS_LABELS[i.status]}
                  </span>
                </TableCell>
                <TableCell className="text-right font-mono">{formatMoney(i.budget)}</TableCell>
                <TableCell className="text-right font-mono">{formatMoney(i.actual)}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(i)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(i)}>
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="investment-dialog">
          <DialogHeader><DialogTitle>{editing ? "Yatırımı Düzenle" : "Yeni Yatırım"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2">
              <Label>Başlık *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                     data-testid="investment-title-input" />
            </div>
            <div>
              <Label>Köy *</Label>
              <Select value={form.village_id} onValueChange={(v) => setForm({ ...form, village_id: v })}>
                <SelectTrigger data-testid="investment-village-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
                <SelectContent>
                  {villages.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Kurum</Label>
              <Select value={form.institution_id} onValueChange={(v) => setForm({ ...form, institution_id: v })}>
                <SelectTrigger><SelectValue placeholder="Seçin (opsiyonel)" /></SelectTrigger>
                <SelectContent>
                  {insts.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tür</Label>
              <Select value={form.investment_type} onValueChange={(v) => setForm({ ...form, investment_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(INVESTMENT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Durum</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INFRA_STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Bütçe (TL)</Label>
              <Input type="number" step="0.01" value={form.budget}
                     onChange={(e) => setForm({ ...form, budget: Number(e.target.value) || 0 })}
                     data-testid="investment-budget-input" />
            </div>
            <div>
              <Label>Gerçekleşen (TL)</Label>
              <Input type="number" step="0.01" value={form.actual}
                     onChange={(e) => setForm({ ...form, actual: Number(e.target.value) || 0 })}
                     data-testid="investment-actual-input" />
            </div>
            <div>
              <Label>Yıl</Label>
              <Input type="number" value={form.year}
                     onChange={(e) => setForm({ ...form, year: Number(e.target.value) || CURRENT_YEAR })} />
            </div>
            <div>
              <Label>Başlangıç</Label>
              <DateField value={form.start_date} className="w-full" testId="investment-start-date-input"
                     onChange={(v) => setForm({ ...form, start_date: v })} />
            </div>
            <div>
              <Label>Bitiş</Label>
              <DateField value={form.end_date} className="w-full" testId="investment-end-date-input"
                     onChange={(v) => setForm({ ...form, end_date: v })} />
            </div>
            <div className="col-span-2">
              <Label>Yapıldığı Yer / Bölge</Label>
              <Input value={form.location || ""} maxLength={200} placeholder="Ör. Aşağı mahalle, köy girişi, okul önü..."
                     onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="investment-location-input" />
            </div>
            <div className="col-span-2 grid grid-cols-3 gap-4">
              <div>
                <Label>Miktar</Label>
                <Input type="number" min="0" step="any" value={form.quantity}
                       onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) || 0 })} data-testid="investment-quantity-input" />
              </div>
              <div>
                <Label>Birim</Label>
                <Input maxLength={30} value={form.unit || ""} placeholder="m, m², adet..."
                       onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="investment-unit-input" />
              </div>
              <div>
                <Label>Birim Fiyat (TL)</Label>
                <Input type="number" min="0" step="0.01" value={form.unit_price}
                       onChange={(e) => setForm({ ...form, unit_price: Number(e.target.value) || 0 })} data-testid="investment-unit-price-input" />
              </div>
            </div>
            <ApplicationFields form={form} setForm={setForm} prefix="investment" />
            <div className="col-span-2">
              <Label>Açıklama</Label>
              <Textarea value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={save} className="bg-emerald-700 hover:bg-emerald-800"
                    data-testid="investment-submit-button">
              {editing ? "Güncelle" : "Oluştur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
